"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateTransformationTitle = exports.calculateTransformationDuration = exports.getLatestProgressPicture = exports.sortProgressPicturesByWeight = exports.sortProgressPicturesByDate = exports.containsProgressPictures = exports.progressPicturesArrayToObject = exports.getTransformationImageCloudPath = exports.getTransformationImageFilename = void 0;
const moment_1 = __importDefault(require("moment"));
const pluralize_1 = __importDefault(require("pluralize"));
const isObject_1 = __importDefault(require("lodash/isObject"));
const isArray_1 = __importDefault(require("lodash/isArray"));
const helpers_1 = require("./helpers");
const validation_1 = require("../validation");
const getTransformationImageFilename = (filename, imageAngle, thumbnailSize = "") => {
    const thumbnailPrefix = `thumb@${thumbnailSize}_`;
    const postFix = `${imageAngle}_${filename}`;
    return thumbnailSize ? thumbnailPrefix + postFix : postFix;
};
exports.getTransformationImageFilename = getTransformationImageFilename;
const getTransformationImageCloudPath = (username, transformationId, progressPictureId, filename) => `users/${username}/transformations/${transformationId}/progress-pictures/${progressPictureId}/${filename}`;
exports.getTransformationImageCloudPath = getTransformationImageCloudPath;
// Converts an Array of Progress Pictures to an Object
const progressPicturesArrayToObject = (progressPicturesArray) => {
    const progressPictures = {};
    let progressPictureId = "";
    progressPicturesArray.forEach((progressPicture) => {
        progressPictureId = (0, helpers_1.generateRandomId)();
        progressPictures[progressPictureId] = progressPicture;
    });
    return progressPictures;
};
exports.progressPicturesArrayToObject = progressPicturesArrayToObject;
// Takes a transformation object and checks if it contains 1 (default) or more progressPictures
// Works for both an Array or Object of progressPictures
const containsProgressPictures = (transformation, n = 1) => {
    const { progressPictures } = transformation;
    if (!progressPictures || !(0, isObject_1.default)(progressPictures))
        return false;
    return (0, isArray_1.default)(progressPictures) ? progressPictures.length >= n : Object.keys(progressPictures).length >= n;
};
exports.containsProgressPictures = containsProgressPictures;
const sortProgressPicturesByDate = (progressPictures = {}, order = "ascending") => {
    // returns list of progressPictureIds sorted by date
    const progressPictureIds = Object.keys(progressPictures);
    progressPictureIds.sort((a, b) => {
        switch (order) {
            case "ascending":
                // @ts-ignore
                return (0, moment_1.default)(progressPictures[a].date) - (0, moment_1.default)(progressPictures[b].date);
            case "descending":
                // @ts-ignore
                return (0, moment_1.default)(progressPictures[b].date) - (0, moment_1.default)(progressPictures[a].date);
            default:
                return 0;
        }
    });
    return progressPictureIds;
};
exports.sortProgressPicturesByDate = sortProgressPicturesByDate;
const sortProgressPicturesByWeight = (progressPictures = {}, order = "descending", weightUnit = validation_1.Weight.KG) => {
    const sortedProgressPictureIds = Object.keys(progressPictures);
    sortedProgressPictureIds.sort((a, b) => {
        var _a, _b;
        const weightA = ((_a = progressPictures[a].weight) === null || _a === void 0 ? void 0 : _a[weightUnit]) || 0;
        const weightB = ((_b = progressPictures[b].weight) === null || _b === void 0 ? void 0 : _b[weightUnit]) || 0;
        return order === "descending" ? weightB - weightA : weightA - weightB;
    });
    return sortedProgressPictureIds;
};
exports.sortProgressPicturesByWeight = sortProgressPicturesByWeight;
const getLatestProgressPicture = (transformation = {}, nLatest = 0, onlyId = false) => {
    // nLatest = 0 -> latest, nLatest = 1 -> 2nd latest, ...
    // returns the nLatest progressPicture or progressPictureId, or null if doesn't exist
    // if onlyId is true, returns the progressPictureId, otherwise returns the progressPicture object
    if (!transformation)
        return null;
    const sortedIds = (0, exports.sortProgressPicturesByDate)(transformation.progressPictures, "descending");
    if (sortedIds.length >= 1 + nLatest) {
        return onlyId ? sortedIds[nLatest] : transformation.progressPictures[sortedIds[nLatest]];
    }
    return null;
};
exports.getLatestProgressPicture = getLatestProgressPicture;
// Calculates the total duration (Object<unit, value>) of the given Transformation Object
// Works for both an Array or Object of progressPictures
const calculateTransformationDuration = (transformation, timeUnit = validation_1.Time.MONTHS, fallbackUnits = [validation_1.Time.WEEKS, validation_1.Time.DAYS]) => {
    // at least two progress pictures are required to calculate the duration
    if (!(0, exports.containsProgressPictures)(transformation, 2))
        return { unit: timeUnit, value: 0 };
    let { progressPictures } = transformation;
    // safety check
    if ((0, isArray_1.default)(progressPictures))
        progressPictures = Object.assign({}, (0, exports.progressPicturesArrayToObject)(progressPictures));
    const sortedProgressPictureIds = (0, exports.sortProgressPicturesByDate)(progressPictures);
    const firstProgressPicture = progressPictures[sortedProgressPictureIds[0]];
    const lastProgressPicture = progressPictures[sortedProgressPictureIds[sortedProgressPictureIds.length - 1]];
    let durationUnit = timeUnit;
    let durationValue = (0, moment_1.default)(lastProgressPicture.date).diff((0, moment_1.default)(firstProgressPicture.date), timeUnit);
    // if the duration is less than 1 unit, try to fall back to a different unit
    if (durationValue < 1) {
        fallbackUnits.every((unit) => {
            durationUnit = unit;
            durationValue = (0, moment_1.default)(lastProgressPicture.date).diff((0, moment_1.default)(firstProgressPicture.date), unit);
            // return false if the duration is greater than 1 unit to stop the loop
            return durationValue < 1;
        });
    }
    return { unit: durationUnit, value: durationValue };
};
exports.calculateTransformationDuration = calculateTransformationDuration;
// Generates a title for the given Transformation Object
const generateTransformationTitle = (username, transformation) => {
    const duration = (0, exports.calculateTransformationDuration)(transformation);
    let durationText = "";
    if ((duration === null || duration === void 0 ? void 0 : duration.value) > 1)
        durationText = `${duration.value} ${(0, pluralize_1.default)(duration.unit, duration.value)}`;
    const title = `${username}'s ${durationText} Transformation`;
    // remove any redundant spacing between words
    return title.replace(/\s+/g, " ");
};
exports.generateTransformationTitle = generateTransformationTitle;
//# sourceMappingURL=transformation.js.map